'use client'

import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { ChevronDown, ChevronRight, CheckCircle2, Circle, Loader } from 'lucide-react'
import { SubjectProgress } from '@/hooks/useStudentProgress'

// ── Dark family (ChildSubjects/ParentDashboard/QuizTable reference) ──
const STUDY_BLUE = '#0EA5E9'
const NAVY = '#F2F5FA'            // INK
const MUTED = '#9AA7BC'
const BORDER = 'rgba(255,255,255,0.10)'
const WHITE = '#0F1930'           // CARD
const GREEN = '#4ADE80'

interface ModuleNode {
    module_id: string
    title: string
    lessons: LessonNode[]
}

interface LessonNode {
    lesson_id: string
    title: string
    learning_objective?: string
    passing_score?: number
}

function buildSyntheticPlan(currentUnit: number, currentLesson: number, t: TFunction): ModuleNode[] {
    return Array.from({ length: Math.max(currentUnit, 3) }, (_, mi) => ({
        module_id: `m${mi + 1}`,
        title: t('education.knowledgeTree.module', { number: mi + 1 }),
        lessons: Array.from({ length: mi < currentUnit - 1 ? 4 : Math.max(currentLesson, 4) }, (_, li) => ({
            lesson_id: `l${mi + 1}_${li + 1}`,
            title: t('education.knowledgeTree.lesson', { number: li + 1 }),
            learning_objective: '',
        })),
    }))
}

function isLessonKeyCompleted(
    lessonId: string | number,
    moduleId: string | number,
    lessonsCompleted: string[],
): boolean {
    const lid = String(lessonId)
    const mid = String(moduleId)
    return lessonsCompleted.includes(lid)                       // bare hierarchical id
        || lessonsCompleted.includes(`u${mid}_l${lid}`)         // prefixed (writer format)
        || lessonsCompleted.includes(`${mid}-${lid}`)           // paired
}

function getLessonStatus(
    lessonId: string | number,
    moduleId: string | number,
    moduleIndex: number,
    lessonIndex: number,
    currentUnit: number,
    currentLesson: number,
    lessonsCompleted: string[],
): 'complete' | 'active' | 'locked' {
    if (isLessonKeyCompleted(lessonId, moduleId, lessonsCompleted)) return 'complete'
    if (moduleIndex + 1 === currentUnit && lessonIndex + 1 === currentLesson) return 'active'
    if (moduleIndex + 1 < currentUnit) return 'complete'
    if (moduleIndex + 1 === currentUnit && lessonIndex + 1 < currentLesson) return 'complete'
    return 'locked'
}

function LessonItem({ lesson, status }: { lesson: LessonNode; status: 'complete' | 'active' | 'locked' }) {
    const { t } = useTranslation()
    const iconColor = status === 'complete' ? GREEN : status === 'active' ? STUDY_BLUE : MUTED

    return (
        <div style={{
            display: 'flex', alignItems: 'flex-start', gap: 10,
            padding: '10px 12px',
            background: status === 'active' ? `${STUDY_BLUE}12` : 'transparent',
            borderRadius: 10,
            borderLeft: status === 'active' ? `3px solid ${STUDY_BLUE}` : '3px solid transparent',
        }}>
            <div style={{ color: iconColor, marginTop: 1, flexShrink: 0 }}>
                {status === 'complete' ? (
                    <CheckCircle2 size={18} />
                ) : status === 'active' ? (
                    <Loader size={18} style={{ animation: 'spin 2s linear infinite' }} />
                ) : (
                    <Circle size={18} />
                )}
            </div>
            <div>
                <p style={{
                    fontSize: 14, fontWeight: status === 'active' ? 700 : 500,
                    color: status === 'locked' ? 'rgba(255,255,255,0.30)' : NAVY,
                    margin: 0,
                }}>
                    {lesson.title}
                </p>
                {lesson.learning_objective && status !== 'locked' && (
                    <p style={{ fontSize: 12, color: MUTED, margin: '2px 0 0', lineHeight: 1.4 }}>
                        {lesson.learning_objective}
                    </p>
                )}
            </div>
            <div style={{ marginLeft: 'auto', flexShrink: 0 }}>
                {status === 'complete' && (
                    <span style={{
                        fontSize: 10, fontWeight: 800, textTransform: 'uppercase',
                        color: GREEN, background: GREEN + '18',
                        padding: '2px 8px', borderRadius: 20,
                    }}>
                        {t('education.common.done')}
                    </span>
                )}
                {status === 'active' && (
                    <span style={{
                        fontSize: 10, fontWeight: 800, textTransform: 'uppercase',
                        color: STUDY_BLUE, background: STUDY_BLUE + '15',
                        padding: '2px 8px', borderRadius: 20,
                    }}>
                        {t('education.common.inProgress')}
                    </span>
                )}
            </div>
        </div>
    )
}

function ModuleSection({ module, index, subject }: { module: ModuleNode; index: number; subject: SubjectProgress }) {
    const { t } = useTranslation()
    const completedInModule = module.lessons.filter((l, li) =>
        getLessonStatus(l.lesson_id, module.module_id, index, li, subject.current_unit, subject.current_lesson, subject.lessons_completed) === 'complete'
    ).length

    const isCurrentModule = index + 1 === subject.current_unit
    const isPastModule = index + 1 < subject.current_unit
    const [open, setOpen] = useState(isCurrentModule)
    const headerColor = isPastModule ? GREEN : isCurrentModule ? STUDY_BLUE : MUTED

    return (
        <div style={{ marginBottom: 8 }}>
            <button
                onClick={() => setOpen(o => !o)}
                style={{
                    width: '100%', border: 'none',
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '12px 14px', borderRadius: 12, cursor: 'pointer',
                    background: open ? `${headerColor}14` : WHITE,
                    transition: 'background 0.15s',
                    textAlign: 'left' as const,
                }}
            >
                <div style={{
                    width: 32, height: 32, borderRadius: 10, flexShrink: 0,
                    background: isPastModule ? GREEN : isCurrentModule
                        ? STUDY_BLUE
                        : BORDER,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: (isPastModule || isCurrentModule) ? WHITE : MUTED,
                    fontSize: 13, fontWeight: 900,
                }}>
                    {isPastModule ? <CheckCircle2 size={16} /> : index + 1}
                </div>
                <div style={{ flex: 1 }}>
                    <p style={{ fontWeight: 700, color: isCurrentModule || isPastModule ? NAVY : MUTED, margin: 0, fontSize: 14 }}>
                        {module.title}
                    </p>
                    <p style={{ fontSize: 11, color: MUTED, margin: 0 }}>
                        {t('education.knowledgeTree.lessonsProgress', {
                            done: completedInModule,
                            total: module.lessons.length,
                        })}
                    </p>
                </div>
                {open ? <ChevronDown size={16} color={MUTED} /> : <ChevronRight size={16} color={MUTED} />}
            </button>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        style={{ overflow: 'hidden', paddingLeft: 8 }}
                    >
                        <div style={{ paddingTop: 4 }}>
                            {module.lessons.map((lesson, li) => (
                                <LessonItem
                                    key={lesson.lesson_id}
                                    lesson={lesson}
                                    status={getLessonStatus(
                                        lesson.lesson_id, module.module_id, index, li,
                                        subject.current_unit, subject.current_lesson,
                                        subject.lessons_completed
                                    )}
                                />
                            ))}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    )
}

interface KnowledgeTreeProps {
    subject: SubjectProgress
}

export default function KnowledgeTree({ subject }: KnowledgeTreeProps) {
    const { t } = useTranslation()

    // Class-enrolled students: the real syllabus lives in module_plan (useStudentProgress
    // unifies class module_plan and solo ai_generated_path into that field). Reading
    // ai_generated_path directly made every class-enrolled student fall through to
    // buildSyntheticPlan — "Lesson 1/2/3" titles, blank objectives, 0/N counts.
    const rawPlan = (Array.isArray(subject.module_plan) && subject.module_plan.length > 0)
        ? subject.module_plan
        : subject.ai_generated_path
    const plan: ModuleNode[] = (Array.isArray(rawPlan) && rawPlan.length > 0)
        ? rawPlan as ModuleNode[]
        : buildSyntheticPlan(subject.current_unit, subject.current_lesson, t)

    if (plan.length === 0) {
        return (
            <div style={{ padding: '24px', textAlign: 'center', color: MUTED, fontSize: 14 }}>
                {t('education.knowledgeTree.noPath')}
            </div>
        )
    }

    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <h4 style={{ fontWeight: 700, color: NAVY, margin: 0, fontSize: 15 }}>
                    {t('education.knowledgeTree.title')}
                </h4>
                <span style={{ fontSize: 12, color: MUTED }}>
                    {t('education.knowledgeTree.position', {
                        unit: subject.current_unit,
                        lesson: subject.current_lesson,
                    })}
                </span>
            </div>

            {plan.map((module, i) => (
                <ModuleSection key={module.module_id} module={module} index={i} subject={subject} />
            ))}
        </div>
    )
}
